// @ts-nocheck
import { useEffect, useState, createContext } from 'react';
import { api } from '@/utils/api';
import { getItem, setItem } from '@/utils/storage';
import { useEventListener } from '@/hooks';
import { Events, Views, ViewValues } from '@/constants';
import { WidgetStateContextType } from '@/types';

export const globalContext = {};

export const WidgetStateContext = createContext<WidgetStateContextType>({
  open: false,
  disableFloatingButton: true,
  bannerClosed: true,
  consentManagementEnabled: false,
  privacyCenterEnabled: false,
  toggleOpen: () => {},
  showFloatingButton: false,
  setBannerClosed: () => {},
  setOpen: () => {},
  selectedView: Views.DataPrivacy,
  setSelectedView: () => {},
  topRef: null as HTMLDivElement | null,
  setTopRef: () => {},
});

function getCordSectionFromUrl() {
  const url = new URL(window.location.href);
  const found = url.searchParams.get('cord_section');
  if (found) {
    url.searchParams.delete('cord_section');
    history.replaceState(history.state, document.title, url.href);
    return found;
  }
  return undefined;
}

export function useWidgetStateContext(
  configContext = {},
  { privacySettingsSet = false } = {},
) {
  const [topRef, setTopRef] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [showFloatingButton, setShowFloatingButton] = useState<boolean>(false);
  const [bannerClosed, setBannerClosedState] = useState<boolean>(
    !!getItem('concord-banner-closed', false),
  );
  const [selectedView, setSelectedView] = useState<ViewValues>(
    Views.DataPrivacy,
  );
  const {
    consentBlockingMode = 'disabled',
    doNotSellRequestEnabled = false,
    enableFloatingButton = false,
    privacyRequestsEnabled = true,
    configLoaded = false,
  } = configContext || {};
  const consentManagementEnabled = consentBlockingMode !== 'disabled';
  const privacyCenterEnabled =
    consentManagementEnabled || privacyRequestsEnabled;
  const disableFloatingButton = !enableFloatingButton || !privacyCenterEnabled;

  /**
   * Toggles the privacy center open and closed. It will default to the consent
   * management view if the privacy settings have not been set. Otherwise, it
   * will default to the data privacy view.
   */
  function toggleOpen() {
    const newOpenState = !open;
    setOpen(newOpenState);
    if (newOpenState && privacyCenterEnabled) {
      const showConsentManagementDefault =
        consentManagementEnabled && !privacySettingsSet;
      setSelectedView(
        showConsentManagementDefault
          ? Views.ConsentManagement
          : Views.DataPrivacy,
      );
    }
  }

  /** Toggles the widget open and sets the view to ConsentManagement. */
  function toggleConsent() {
    setSelectedView(Views.ConsentManagement);
    setOpen(true);
  }

  useEffect(() => {
    const showButton = consentManagementEnabled ? bannerClosed && !open : !open;
    setShowFloatingButton(showButton);
  }, [open, consentManagementEnabled, bannerClosed]);

  function setBannerClosed(value: boolean) {
    setBannerClosedState(value);
    setItem('concord-banner-closed', value);
  }

  useEventListener(Events.Open, () => setOpen(true));
  useEventListener(Events.Close, () => setOpen(false));
  useEventListener(Events.Toggle, () => setOpen(!open));
  useEventListener(Events.ToggleConsent, toggleConsent);
  useEventListener(api.events.Unavailable, () => setOpen(false));

  /**
   * The following code is responsible for setting the view state based on the
   * URL if cord_section is present and the privacy center is enabled.
   */
  useEffect(() => {
    function setViewState() {
      if (configLoaded) {
        const section = getCordSectionFromUrl();
        if (section) {
          if (section === 'privacy_center') {
            setSelectedView(Views.DataPrivacy);
            setOpen(true);
            return;
          }
          if (section === 'privacy_options') {
            setSelectedView(Views.PrivacyOptionsSelector);
            setOpen(true);
            return;
          }
          if (section === 'consent_settings' && consentManagementEnabled) {
            setSelectedView(Views.ConsentManagement);
            setOpen(true);
            return;
          }
          if (section === 'privacy_options_dns' && doNotSellRequestEnabled) {
            setSelectedView(Views.ComplianceRequestDoNotSell);
            setOpen(true);
            return;
          }
          if (privacyCenterEnabled) {
            const showConsentManagementDefault =
              consentManagementEnabled && !privacySettingsSet;
            setSelectedView(
              showConsentManagementDefault
                ? Views.ConsentManagement
                : Views.DataPrivacy,
            );
            setOpen(true);
          }
        }
      }
    }

    setViewState();
  }, [window.location.href, configLoaded]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(
        open
          ? window?.concord?.plugin?.widget?.events?.Opened
          : window?.concord?.plugin?.widget?.events?.Closed,
      ),
    );
  }, [open]);

  return {
    open,
    disableFloatingButton,
    bannerClosed,
    consentManagementEnabled,
    privacyCenterEnabled,
    toggleConsent,
    toggleOpen,
    showFloatingButton,
    setBannerClosed,
    setOpen,
    selectedView,
    setSelectedView,
    topRef,
    setTopRef,
  };
}
